// material-ui
import React, { useState } from "react";
import {
  Stack,
  Box,
  IconButton,
  Tooltip,
  Avatar,
  Typography,
  CircularProgress,
} from "@mui/material";
import { keyframes } from "@mui/system";
// project imports
import { useDispatch, useSelector } from "store";
import { getPlayerBalance, getWithdrawHistory } from "store/slices/user";
import LogoSection from "../LogoSection";
import useAuth from "hooks/useAuth";
import axiosex from "axios";
import { C, G, FONT_DISPLAY, formatMoney } from "themes/aurora";
// MUI icons
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import walletIcon from "assets/images/dashboard/money_bag_3d.png";

const slide = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`;

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

const Header = () => {
  const { logout, user } = useAuth();
  const dispatch = useDispatch();
  const { playerBalance, withdrawHistory } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const hasCheckingWithdraw =
    Array.isArray(withdrawHistory) &&
    withdrawHistory.some((item) => item.status === "CHECKING");

  React.useEffect(() => {
    if (!user) return;
    dispatch(getPlayerBalance(user));
    dispatch(getWithdrawHistory(user.id));
  }, [dispatch, user]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const handleClickRefresh = async () => {
    setLoading(true);
    const delay = (ms) => new Promise((res) => setTimeout(res, ms));
    try {
      await axiosex.get(
        "https://api.ufavipthai.com/userbalance/updatebalance/" + user.id,
      );
      await delay(5000);
      await dispatch(getPlayerBalance(user));
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 10000);
    }
  };

  const initial = (user?.username || "?").slice(0, 1).toUpperCase();

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1240,
        mx: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 2, sm: 3 },
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          "& img": { display: "block", maxHeight: { xs: 34, sm: 40 } },
        }}
      >
        <LogoSection />
      </Box>

      {/* Right Section */}
      <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }}>
        {/* Balance pill */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: 1.25,
            pl: 0.75,
            pr: 0.5,
            py: 0.5,
            borderRadius: "999px",
            background: "rgba(255,255,255,0.04)",
            border: `1px solid ${C.border}`,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              background: "rgba(255,200,87,0.12)",
            }}
          >
            {hasCheckingWithdraw ? (
              <HourglassTopRoundedIcon sx={{ fontSize: 18, color: C.gold }} />
            ) : (
              <Box
                component="img"
                src={walletIcon}
                alt=""
                sx={{ width: 22, height: 22 }}
              />
            )}
          </Box>
          <Box sx={{ lineHeight: 1 }}>
            <Typography
              sx={{
                fontSize: 10.5,
                color: C.textMute,
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {hasCheckingWithdraw ? "สถานะ" : "ยอดเงินคงเหลือ"}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_DISPLAY,
                fontSize: 16,
                fontWeight: 700,
                color: hasCheckingWithdraw ? C.gold : C.text,
                lineHeight: 1.2,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {hasCheckingWithdraw
                ? "รอตรวจสอบถอน"
                : `฿ ${formatMoney(playerBalance)}`}
            </Typography>
          </Box>
          <Tooltip title="รีเฟรชยอดเงิน" arrow>
            <span>
              <IconButton
                aria-label="refresh-balance"
                onClick={handleClickRefresh}
                disabled={loading}
                size="small"
                sx={{
                  width: 34,
                  height: 34,
                  color: C.text,
                  background: G.primary,
                  "&:hover": {
                    background: G.primary,
                    filter: "brightness(1.1)",
                  },
                  "&.Mui-disabled": {
                    background: "rgba(255,255,255,0.06)",
                    color: C.textDim,
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={14} sx={{ color: C.textDim }} />
                ) : (
                  <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* User chip */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            pl: 0.5,
            pr: { xs: 0.5, md: 1.5 },
            py: 0.5,
            borderRadius: "999px",
            background: "rgba(255,255,255,0.04)",
            border: `1px solid ${C.border}`,
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: 15,
                color: "#fff",
                background: G.play,
              }}
            >
              {initial}
            </Avatar>
            <Box
              sx={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: C.mint,
                border: `2px solid ${C.bg}`,
              }}
            />
          </Box>
          <Typography
            noWrap
            sx={{
              display: { xs: "none", md: "block" },
              maxWidth: 140,
              fontSize: 13.5,
              fontWeight: 600,
              color: C.text,
            }}
          >
            {user?.username}
          </Typography>
        </Stack>

        {/* Logout */}
        <Tooltip title="ออกจากระบบ" arrow>
          <IconButton
            aria-label="logout"
            onClick={handleLogout}
            sx={{
              width: 42,
              height: 42,
              borderRadius: "14px",
              color: C.textDim,
              border: `1px solid ${C.border}`,
              background: "rgba(255,255,255,0.04)",
              transition: "all .2s ease",
              "&:hover": {
                color: "#fff",
                background: "rgba(255,107,139,0.18)",
                borderColor: "rgba(255,107,139,0.45)",
              },
            }}
          >
            <LogoutRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            bottom: 0,
            width: "40%",
            height: 2,
            background: G.primary,
            animation: `${slide} 1.4s ease-in-out infinite`,
          }}
        />
      )}
    </Box>
  );
};

export default Header;
